import React from 'react'
import { SUPERMARKETS } from '@utils/constants'
import styles from './SupermarketBadge.module.css'

/** Supermarkets with real logo files in /public/logos/ */
const REAL_LOGOS = new Set(['ah', 'aldi', 'dirk', 'ekoplaza', 'jumbo', 'lidl', 'odin', 'picnic', 'dekamarkt'])

/** Inline SVG fallbacks for supermarkets without a real logo file */
const INLINE_LOGOS = {
  plus: (
    <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
      <title>Plus</title>
      <path d="M7 3h2v10H7z" />
      <path d="M3 7h10v2H3z" />
    </svg>
  ),
}

function getBadgeStyles(meta, bgColor, borderColor, { isXs, iconOnly }) {
  if (iconOnly) {
    return {
      backgroundColor: bgColor,
      color: meta.color,
      border: `1px solid ${borderColor}`,
      padding: '4px',
      borderRadius: '50%',
    }
  }
  return {
    backgroundColor: bgColor,
    color: meta.color,
    border: `1px solid ${borderColor}`,
    fontSize: isXs ? '0.5625rem' : '0.6875rem',
    padding: isXs ? '1px 5px' : '3px 8px',
    gap: isXs ? '3px' : '4px',
  }
}

export function SupermarketBadge({ supermarket, size = 'sm', iconOnly = false, layoutClassName = undefined }) {
  const meta = SUPERMARKETS[supermarket] ?? {
    id: supermarket,
    shortLabel: supermarket.charAt(0).toUpperCase() + supermarket.slice(1),
    color: idToColor(supermarket),
  }

  const bgColor = colorWithAlpha(meta.color, 0.15)
  const borderColor = colorWithAlpha(meta.color, 0.3)

  const hasRealLogo = REAL_LOGOS.has(supermarket)
  const inlineSvg = INLINE_LOGOS[supermarket] ?? null
  const logoSize = iconOnly ? 18 : 14
  const logo = hasRealLogo
    ? <img src={`/logos/${supermarket}.svg`} alt={meta.shortLabel} className={styles.logoImg} style={iconOnly ? { width: logoSize, height: logoSize } : undefined} />
    : inlineSvg ? React.cloneElement(inlineSvg, { className: styles.logoSvg, width: logoSize, height: logoSize }) : null

  const isXs = size === 'xs'

  return (
    <span
      className={`${styles.component} ${layoutClassName || ''}`}
      style={getBadgeStyles(meta, bgColor, borderColor, { isXs, iconOnly })}
      title={iconOnly ? meta.shortLabel : undefined}
    >
      {logo && <span className={styles.logo}>{logo}</span>}
      {!iconOnly && meta.shortLabel}
    </span>
  )
}

function colorWithAlpha(color, alpha) {
  if (color.startsWith('#')) {
    const r = Number.parseInt(color.slice(1, 3), 16)
    const g = Number.parseInt(color.slice(3, 5), 16)
    const b = Number.parseInt(color.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }
  return `color-mix(in srgb, ${color} ${Math.round(alpha * 100)}%, transparent)`
}

function idToColor(id) {
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash)
  }
  return `hsl(${Math.abs(hash) % 360}, 65%, 50%)`
}
